import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { api } from '../api';

const FeedbackAnalytics = () => {
  const { t } = useTranslation();
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const data = await api.request('/admin/feedback');
      setFeedbackData(data);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter feedback
  const filteredFeedback = feedbackData?.feedback?.filter(f => {
    const matchesType = !typeFilter || f.feedback_type === typeFilter;
    const matchesRating = !ratingFilter || 
      (ratingFilter === 'positive' && f.rating >= 4) ||
      (ratingFilter === 'negative' && f.rating <= 2) ||
      (ratingFilter === 'neutral' && f.rating === 3);
    return matchesType && matchesRating;
  }) || [];

  const renderStars = (rating) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const getTypeColor = (type) => {
    const colors = {
      quick_rating: 'bg-blue-100 text-blue-700',
      feature_request: 'bg-purple-100 text-purple-700',
      bug_report: 'bg-red-100 text-red-700',
      general: 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-12 h-12 border-4 border-levelup-accent border-t-transparent rounded-full mx-auto"></div>
        <p className="text-gray-500 mt-4">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-levelup-dark rounded-xl p-4 text-white">
        <h1 className="text-xl font-bold">{t('feedbackAnalytics.title')}</h1>
        <p className="text-gray-300 mt-1">{t('feedbackAnalytics.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('feedbackAnalytics.totalFeedback')}</p>
          <p className="text-xl font-bold text-levelup-dark">{feedbackData?.stats?.total_feedback || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('feedbackAnalytics.avgRating')}</p>
          <div className="flex items-center gap-2">
            <p className="text-xl font-bold text-levelup-dark">{feedbackData?.stats?.avg_rating || 0}</p>
            {renderStars(Math.round(feedbackData?.stats?.avg_rating || 0))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('feedbackAnalytics.positiveRatings')}</p>
          <p className="text-xl font-bold text-green-600">{feedbackData?.stats?.positive_count || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-md">
          <p className="text-gray-500 text-sm">{t('feedbackAnalytics.negativeRatings')}</p>
          <p className="text-xl font-bold text-red-600">{feedbackData?.stats?.negative_count || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('feedbackAnalytics.filterByType')}</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
            >
              <option value="">{t('feedbackAnalytics.allTypes')}</option>
              <option value="quick_rating">{t('feedback.types.quick_rating')}</option>
              <option value="feature_request">{t('feedback.types.feature_request')}</option>
              <option value="bug_report">{t('feedback.types.bug_report')}</option>
              <option value="general">{t('feedback.types.general')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('feedbackAnalytics.filterByRating')}</label>
            <select
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
            >
              <option value="">{t('feedbackAnalytics.allRatings')}</option>
              <option value="positive">{t('feedbackAnalytics.positiveRatings')}</option>
              <option value="neutral">Neutral (3 stars)</option>
              <option value="negative">{t('feedbackAnalytics.negativeRatings')}</option>
            </select>
          </div>
          {(typeFilter || ratingFilter) && (
            <div className="flex items-end">
              <button
                onClick={() => { setTypeFilter(''); setRatingFilter(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
              >
                {t('common.clear')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="px-4 py-2.5 border-b">
          <h2 className="text-lg font-bold text-levelup-dark">{t('feedbackAnalytics.allFeedback')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.user')}</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.domain')}</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.rating')}</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.type')}</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.message')}</th>
                <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700">{t('feedbackAnalytics.date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredFeedback.length > 0 ? (
                filteredFeedback.map((f, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-700">{f.user_email?.split('@')[0] || 'Unknown'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{f.user_domain || '-'}</td>
                    <td className="px-3 py-2">{renderStars(f.rating)}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(f.feedback_type)}`}>
                        {t(`feedback.types.${f.feedback_type}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate">{f.message || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-500">
                      {new Date(f.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    {t('feedbackAnalytics.noFeedback')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeedbackAnalytics;
